import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Button,
  InputBase,
  CircularProgress,
  Alert,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Delete,
  DeleteOutline,
  ErrorOutline,
  KeyboardArrowDown,
} from "@mui/icons-material";
import axios from "axios";
import DeleteRecordingModal from "../modals/DeleteRecordingModal";
import AudioDuration from "../modals/AudioDuration";
import AudioPlayer from "../modals/AudioPlayer";
import FileDownloader from "../modals/FileDownloader";
import posthog from "posthog-js";
import { Download, Mic, Play, Square, StopCircle } from "lucide-react";

const Recordings = () => {
  const [record_type, setRecordType] = useState("upload_mp3");
  const [uploadedFile, setUploadedFile] = useState(null);

  const [buttonState, setButtonState] = useState("record"); // "record", "stop", "play"
  const [time, setTime] = useState(0); // Timer in seconds
  const [maxTime, setMaxTime] = useState(30); // Timer in seconds
  const [audioBlob, setAudioBlob] = useState(null); // Stores the recorded audio
  const [isRecording, setIsRecording] = useState(false); // Tracks recording state
  const mediaRecorderRef = useRef(null); // MediaRecorder reference
  const audioUrlRef = useRef(null); // URL for the audio playback
  const mediaStreamRef = useRef(null); // Stores the media stream
  const [recording_data, setRecordingData] = useState({
    title: "",
    type: "",
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const chunksRef = useRef([]);
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef(null);


  // Handle button click to toggle states and recording
  const handleClick = () => {
    if (buttonState === "record") {
      startRecording();
      setButtonState("stop");
    } else if (buttonState === "stop") {
      stopRecording();
      setButtonState("play");
    } else if (buttonState === "play") {
      playRecording();
      setButtonState('playing')
    } else if (buttonState === "playing") {
      stopPlaying();
      setButtonState("play");
    }
  };

  const startRecording = async () => {
    try {
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await processAudioBlob(audioBlob);
      };

      mediaRecorder.onstart = () => {
        setIsRecording(true);
        mediaRecorder.requestData();
        setTimeout(() => {
          if (mediaRecorder.state === "recording") {
            stopRecording();
          }
        }, 30000);
      };

      mediaRecorder.start(1000);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
    }
  };

  const processAudioBlob = async (blob) => {
    try {
      setIsConverting(true);
      const audioContext = new AudioContext();
      const audioBuffer = await audioContext.decodeAudioData(await blob.arrayBuffer());
      const wavBlob = await convertToWav(audioBuffer);

      setAudioBlob(wavBlob);
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
      }
      audioUrlRef.current = URL.createObjectURL(wavBlob);
      setIsRecording(false);
    } catch (error) {
      console.error("Error processing audio:", error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const maxSize = 25 * 1024 * 1024; // 5 MB in bytes

    if (file) {
      // Check file size
      if (file.size > maxSize) {
        alert(
          "Uploaded file too large. Please upload a file less than 25MB"
        );
        event.target.value = ""; // Clear the file input
        setUploadedFile(null);
      } else {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['mp3', 'm4a', 'wav'];
        if (!allowedExtensions.includes(fileExtension)) {
          alert('Unsupported file type detected. Please upload mp3, m4a, or wav.');
          event.target.value = ''; // Clear the file input
        } else {
          try {
            setIsConverting(true);
            setUploadedFile(file); // Set the uploaded file if valid
            await processAudioBlob(file);
          } catch (error) {
            console.error("Error processing uploaded file:", error);
          }

        }
      }
    }


  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
      mediaStreamRef.current?.getTracks().forEach(track => track.stop());
    }
  };

  const convertToWav = async (audioBuffer) => {
    const offlineCtx = new OfflineAudioContext(1, audioBuffer.duration * 8000, 8000);
    const source = offlineCtx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start();

    const renderedBuffer = await offlineCtx.startRendering();
    const wavBuffer = createWAVFile(renderedBuffer);
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };

  const createWAVFile = (audioBuffer) => {
    const numChannels = 1;
    const sampleRate = 8000;
    const bitDepth = 16;
    const length = audioBuffer.length;

    const buffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(buffer);

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * (bitDepth / 8), true);
    view.setUint16(32, numChannels * (bitDepth / 8), true);
    view.setUint16(34, bitDepth, true);
    writeString(view, 36, 'data');
    view.setUint32(40, length * 2, true);

    const channelData = audioBuffer.getChannelData(0);
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const sample = Math.max(-1, Math.min(1, channelData[i]));
      view.setInt16(offset, sample * 0x7FFF, true);
      offset += 2;
    }

    return buffer;
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const downloadAudio = () => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'recording.wav';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Stop recording and turn off the microphone
  // const stopRecording = () => {
  //   setIsRecording(false); // Stop the timer
  //   if (mediaRecorderRef.current) {
  //     mediaRecorderRef.current.stop();
  //   }
  //   // Stop the microphone stream
  //   if (mediaStreamRef.current) {
  //     mediaStreamRef.current.getTracks().forEach((track) => track.stop()); // Stop all tracks (including audio)
  //   }
  // };

  const audioRef = useRef(null);
  const intervalIdRef = useRef(null);

  const [currentTime, setCurrentTime] = useState(0);

  const playRecording = () => {
    if (audioUrlRef.current) {
      if (!audioRef.current) {
        // Create a new audio instance if not already created
        audioRef.current = new Audio(audioUrlRef.current);

        // Set the max time to the length of the audio (in seconds)
        setMaxTime(30);
      }

      audioRef.current.currentTime = 0; // Start from the beginning of the audio
      audioRef.current.play();

      // Clear any previous interval and start a new timer
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }

      intervalIdRef.current = setInterval(() => {
        // Update current time based on audio playback
        setCurrentTime(audioRef.current.currentTime);
        if (audioRef.current.currentTime >= maxTime) {
          clearInterval(intervalIdRef.current); // Stop the timer once the audio finishes
          intervalIdRef.current = null;
        }


      }, 1000); // Update every second to match audio playback


      audioRef.current.onended = () => {
        // Update the button state to "playing"
        setButtonState("play"); // Make sure you have this state defined
        stopPlaying(); // Stop the audio and the timer
      };
    }
  };

  const stopPlaying = () => {
    if (audioRef.current) {
      audioRef.current.pause(); // Pause the audio
    }
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current); // Stop the timer
      intervalIdRef.current = null;
    }
  };



  // Restart recording (reset state and start over)
  const restartRecording = () => {
    console.log('k')
    setUploadedFile(null)
    stopRecording(); // First stop any ongoing recording
    setTime(0); // Reset the timer
    setAudioBlob(null); // Clear the previous audio
    setButtonState("record"); // Reset to "record" button
    setMaxTime(30);
    mediaStreamRef.current = null
    audioRef.current = null
    audioUrlRef.current = null
    mediaRecorderRef.current = null
    intervalIdRef.current = null
  };

  const [successMsg, setSuccessMsg] = useState(null)
  const storeRecording = async () => {
    let check = await axios.post(`${process.env.REACT_APP_BACKEND_URL}get-user-by-id`, {
      userId: JSON.parse(localStorage.getItem('user')).userId.S
    });

    if (check.status === 200) {
      const user___ = check.data.user;


      if (user___.relatedOrgs.L?.find((e) => e?.M.org_id?.S === JSON.parse(localStorage.getItem('organization')).orgId.S)) {
        try {
          setLoader(true);
          console.log(uploadedFile);
          const formData = new FormData();
          if (audioBlob) {
            // const url = URL.createObjectURL(audioBlob);
            // const a = document.createElement('a');
            // a.href = url;
            // a.download = 'recording.wav';
            // document.body.appendChild(a);
            // a.click();
            // document.body.removeChild(a);
            // URL.revokeObjectURL(url);
            console.log('audioBlob', audioBlob)
            console.log(recording_data.title)
            formData.append("file", audioBlob, `${recording_data.title}-${Date.now()}.${audioBlob.type.split('/')[1]}`);
          } else {
            formData.append("file", uploadedFile, `${recording_data.title}-${Date.now()}.${uploadedFile.name.split('.').pop().toLowerCase()}`);
          }


          const record = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "upload",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          console.log("record", record);
          let recording = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "add-recording",
            {
              title: recording_data.title,
              type: record_type,
              url: record.data.url,
              organization_id: JSON.parse(localStorage.getItem("organization"))
                .orgId.S,
            }
          );

          localStorage.setItem(
            "organization",
            JSON.stringify(recording.data.updatedItem)
          );

          setRecordings(recording.data.updatedItem.recordings.L);
          restartRecording()
          setRecordingData({ title: '' })
          setLoader(false);
          setErrorMsg(null)
          setSuccessMsg('Record was successfully stored to organization')
          //post hog track event for recording
          posthog.capture('Added a recording', {
            email: JSON.parse(localStorage.getItem('user')).email.S,
            userId: JSON.parse(localStorage.getItem('user')).userId.S,
          });
          setTimeout(() => {
            setSuccessMsg(null)
          }, 3000);

          console.log("recording", recording);
        } catch (error) {
          console.log(error);
          if (error.response && error.response.data) {
            console.error("Response data:", error.response.data);
            setErrorMsg(error.response.data.message);
          }
          setLoader(false);
        }

      } else {
        window.location.reload()
      }

    }
  };


  const playAudio = () => {
    if (audioRef.current && audioUrlRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };





  // Timer logic for recording
  useEffect(() => {
    let timerInterval;
    let t = 0;
    if (isRecording) {
      timerInterval = setInterval(() => {
        if (t < maxTime) {
          t += 1;
          setTime((prev) => prev + 1);
        } else {
          stopRecording();
        }
      }, 1000);
    } else {
      clearInterval(timerInterval);
    }
    return () => clearInterval(timerInterval);
  }, [isRecording]);

  // Format time as mm:ss
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? `0${secs}` : secs}`;
  };
  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false);

  //recordings

  const [recordings, setRecordings] = useState(
    JSON.parse(localStorage.getItem("organization")).recordings?.L || []
  );
  return (
    <div className="pt-7 px-4">
      <div className="text-[17px] font-semibold">Recordings</div>

      <div className="bg-white rounded-[13px] w-full my-5 lg:px-12 px-4 py-6 space-y-4">
        <div className="lg:text-[15px] text-[14px] text-[#030229] font-semibold">
          Add Recording
        </div>

        {err_msg && (
          <div className="text-center text-red-500 lg:text-[13px] text-[12px] mt-2  font-semibold ">
            <ErrorOutline sx={{ fontSize: "" }} /> {err_msg}
          </div>
        )}

        <div className="lg:ml-8 space-y-2">
          <div className="lg:w-[200px]">
            <Select
              IconComponent={KeyboardArrowDown}
              fullWidth
              value={record_type}
              onChange={(e) => setRecordType(e.target.value)}
              className="mb-2 bg-[#F7F7F8]"
              sx={{
                fontSize: "13px",
                padding: "2px 8px", // Adjust padding here
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "& .MuiSelect-select": {
                  padding: "2px 8px", // Adjust padding for the text inside Select
                },
              }}
            >
              <MenuItem value="upload_mp3">Upload MP3</MenuItem>
              <MenuItem value="record_now">Record Now</MenuItem>
            </Select>
          </div>

          <div className=" lg:flex items-center w-full">
            <div className="lg:flex-1 ">
              {record_type === "upload_mp3" && (
                <div className=" flex items-center space-x-4 lg:ml-[80px] mb-2 lg:mb-0">
                  {/* <input
                    hidden
                    type="file"
                    id="upload_record"
                   accept=".mp3, .m4a, .wav"
                    onChange={(e) => {
                      const file = e.target.files[0];

                      const maxSize = 25 * 1024 * 1024; // 5 MB in bytes

                      if (file) {
                        // Check file size
                        if (file.size > maxSize) {
                          alert(
                            "Uploaded file too large. Please upload a file less than 25MB"
                          );
                          e.target.value = ""; // Clear the file input
                          setUploadedFile(null);
                        } else {
                          const fileExtension = file.name.split('.').pop().toLowerCase();
                          const allowedExtensions = ['mp3', 'm4a', 'wav'];
                          if (!allowedExtensions.includes(fileExtension)) {
                            alert('Unsupported file type detected. Please upload mp3, m4a, or wav.');
                            e.target.value = ''; // Clear the file input
                        }else{
                          setUploadedFile(file); // Set the uploaded file if valid
                        }
                        }
                      }
                    }}
                  />
                  <img
                    src="/icons/upload.png"
                    className="lg:w-[50px] w-[30px] "
                    onClick={() => {
                      document.getElementById("upload_record").click();
                    }}
                  />

                  <div className="lg:text-[14px] text-[13px] italic ">
                    {" "}
                    {uploadedFile ? uploadedFile.name : "No file uploaded"}{" "}
                  </div> */}


                  <div className="relative inline-block">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="audio/*"
                      className="hidden"
                      disabled={isRecording || isConverting}
                    />
                    {/* <button
                onClick={() => fileInputRef.current?.click()}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                disabled={isRecording || isConverting}
              >
                <Upload size={20} />
                <span>Upload Audio</span>
              </button> */}

                    <img
                      src="/icons/upload.png"
                      className="lg:w-[50px] w-[30px] "
                      onClick={() => fileInputRef.current?.click()}
                    />

                    <div className="lg:text-[14px] text-[13px] italic ">
                      {" "}
                      {uploadedFile ? uploadedFile.name : "No file uploaded"}{" "}
                    </div>
                  </div>
                </div>
              )}

              {record_type === "record_now" && (
                <div className="flex items-center space-x-1 mb-2 lg:mb-0">
                  <div className="audio-input-ui">
                    <button
                      className={`record-button ${buttonState}`}
                      onClick={handleClick}
                    ></button>
                    <span className="audio-timer">
                      {formatTime(time)}/{formatTime(maxTime)}
                    </span>
                  </div>

                  {/* <div className="audio-input-ui">
                    <button
                      className={`record-button ${buttonState}`}
                      onClick={handleClick}
                    ></button>
                    <span className="audio-timer">
                      {formatTime(time)}/{formatTime(maxTime)}
                    </span>
                  </div> */}

                  {buttonState === "play" && (
                    <DeleteOutline
                      className="lg:w-[17px] w-[15px] cursor-pointer"
                      onClick={() => {
                        restartRecording();
                      }}
                    />
                  )}
                </div>
              )}
            </div>

            <div className="lg:w-[240px] w-full lg:mr-5 mb-2 lg:mb-0">
              <InputBase
                placeholder="Recording name"
                value={recording_data.title}
                onChange={(e) =>
                  setRecordingData({ ...recording_data, title: e.target.value })
                }
                sx={{
                  bgcolor: "#F7F7F8",
                  width: "100%",
                  px: 2,
                  fontSize: "14px",
                }}
              />
            </div>

            <div>
              <Button
                sx={{
                  textTransform: "none",
                  bgcolor: "#605BFF",
                  color: "white",
                  py: "6px",
                  px: "8px",
                  borderRadius: "7px",
                  fontSize: { lg: "14px", xs: "12px" },
                }}
                disabled={loader || ((record_type === 'upload_mp3' && !uploadedFile) ||
                  (record_type === 'record_now' && !audioBlob))}
                onClick={() => {
                  if (recording_data.title.length < 1) {
                    setErrorMsg('no record title')
                  } else {
                    storeRecording()
                  }
                }}
              >
                {loader ? (
                  <CircularProgress size={"1.3rem"} sx={{ color: "white", animationDuration: '0.7s' }} />
                ) : (
                  "  Save Recording"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[13px] px-10 py-6 w-full">
        <div className="lg:text-[15px] text-[13px] text-[#030229] ml-2 font-semibold ">
          Recording List
        </div>
        {successMsg && (

          <Alert color="success" sx={{ ml: 1 }} >
            <div className='text-[14px] '>
              {successMsg}
            </div>
          </Alert>
        )}
        {recordings && recordings.length > 0 ? (
          <RecordingList recordings={recordings} setRecordings={setRecordings} />
        ) : (
          <Alert color="info" sx={{ ml: 1 }} >
            <div className='text-[14px] '>
              You do not have any recordings created yet. Add a recording above to get started. Need help? <a className="hover:text-red-600 hover:underline" href='mailto:hello@twilanswer.com'>Contact Support</a>.
            </div>
          </Alert>
        )}

      </div>
    </div>
  );
};

export default Recordings;
const RecordingList = ({ recordings, setRecordings }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [openModalIndex, setOpenModalIndex] = useState(null); // Track which row's modal is open
  const [loader, setLoader] = useState(false);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const sortedRows = [...recordings].sort((a, b) => {
    if (sortConfig.key) {
      const isAscending = sortConfig.direction === "asc";
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return isAscending ? -1 : 1;
      if (aValue > bValue) return isAscending ? 1 : -1;
    }
    return 0;
  });

  function formatTimestamp(timestamp) {
    const date = new Date(Number(timestamp));
    if (isNaN(date.getTime())) return "Invalid timestamp";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`;
  }

  return (
    <TableContainer className="w-full overflow-x-auto lg:overflow-hidden">
      <Table style={{ minWidth: "1100px" }}>
        <TableHead className="bg-white">
          <TableRow>
            <TableCell onClick={() => handleSort("created")}>
              Created
            </TableCell>
            <TableCell onClick={() => handleSort("name")}>Name</TableCell>
            <TableCell onClick={() => handleSort("Duration")}>
              Duration
            </TableCell>
            <TableCell>Play</TableCell>
            <TableCell>Download</TableCell>
            <TableCell>Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row, index) => (
            <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-[#FAFAFB]"}>
              <TableCell>{formatTimestamp(row.M.last_updated.N)}</TableCell>
              <TableCell>{row.M.title.S}</TableCell>
              <TableCell>
                <AudioDuration url={row.M.url.S} />
              </TableCell>
              <TableCell>
                <AudioPlayer url={row.M.url.S} />
              </TableCell>
              <TableCell>
                {/* {row.M.url.S} */}
                <FileDownloader url={row.M.url.S} title={row.M.title.S} />
              </TableCell>
              <TableCell>
                <img
                  src="/icons/delete_2.png"
                  className="w-[24px] cursor-pointer"
                  onClick={() => setOpenModalIndex(index)} // Set the current row index
                />
                {openModalIndex === index && (
                  <DeleteRecordingModal
                    loader={loader}
                    setLoader={setLoader}
                    open={true} // Always open for the current index
                    setOpen={() => setOpenModalIndex(null)} // Close modal
                    recording_id={row.M.rec_id.S}
                    setRecordings={setRecordings}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
